import { EventInput } from "@fullcalendar/core/index.js";
import { Dispatch, SetStateAction } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { calculateAge } from "@utilifiles/methods/dateMethods";
import { toUtcZ } from "@utilifiles/methods/localDateTime";
import { CountryType } from "../../Patientregistration/v2/type/RegistrationDataType";
import { resolveCountryId } from "./countryCodeUtils";
dayjs.extend(utc);
export type TimeSlotType = {
  endTime: string | null | undefined;
  timeSlotNo: number;
  startTime: string | null;
  times: string | null | undefined;
  slotType: "dayGridMonth" | "timeGridWeek" | "timeGridDay" | string;
  doctorId?: number;
  doctor?: {
    doctorId: number;
    doctorName: string;
    color?: string;
  };
  department?: {
    departmentId: number
    departmentName: string

  },
  visitType?: string | null
  notes?: string | null;
  email?: string | null;
  lockedDoctor?: boolean;
  lockedDepartment?: boolean;
  lockedTimeSLot?: boolean;
  lockedDate?: boolean;
};

export type DoctorsType = {
  doctorId: number;
  doctorName: string;
  color: string;
  duration: string;
  startTime: string;
  endTime: string;
};

export type AllDoctorsType = {
  departmentId: number;
  departmentName: string;
  doctor: SearchParamsDoctorType[];
};

export type DepartmentTypes = {
  departmentId: number;
  departmentCode: string;
  departmentName: string;
  isRefRequired?: boolean;
  skipVitals?: boolean;
  isExternalRef?: boolean;
  clinicId?: number;
  createdById?: number;
  createdByDate?: string;
  deptStatus?: number;
};

export type BookingListType = EventInput;
export type SearchParamsDoctorType = {
  doctorId: number;
  doctorName: string;
  color: string;
  duration: number;
  timeslots: {
    dayId: number;
    startTime: string;
    endTime: string;
  }[];
};

export const mapBackendDoctorToSearchParam = (
  doctor: {
    doctorId: number;
    doctorName: string;
    color: string;
    durationForPatient?: number;
  },
): SearchParamsDoctorType => ({
  doctorId: doctor.doctorId,
  doctorName: doctor.doctorName,
  color: doctor.color,
  duration: doctor.durationForPatient ?? 12,
  timeslots: [],
});

export const groupDoctorsByDepartment = (
  doctors: {
    doctorId: number;
    doctorName: string;
    color: string;
    departmentId?: number;
    departmentName?: string;
    durationForPatient?: number;
  }[],
) => {
  const grouped = new Map<
    number,
    {
      departmentId: number;
      departmentName: string;
      doctor: SearchParamsDoctorType[];
    }
  >();

  doctors.forEach((doctor) => {
    const departmentId = doctor.departmentId ?? 0;
    const departmentName = doctor.departmentName?.trim() || "Other";
    if (!grouped.has(departmentId)) {
      grouped.set(departmentId, {
        departmentId,
        departmentName,
        doctor: [],
      });
    }
    grouped.get(departmentId)!.doctor.push(mapBackendDoctorToSearchParam(doctor));
  });

  return Array.from(grouped.values());
};
export type BookingParamsType = {
  startTime: string;
  endTime: string;
  department: { departmentId: number; departmentName: string } | null;
  doctor: SearchParamsDoctorType | null;
};

export type SelectedSlotType = {
  slotType:
  | "dayGridMonth"
  | "timeGridWeek"
  | "timeGridDay"
  | "multiMonthYear"
  | string;
} & TimeSlotType;

export type PatientBookingContextType = {
  timeSlot: SelectedSlotType;
  setTimeSlot: Dispatch<SetStateAction<SelectedSlotType>>;
  bookingList: BookingListType[];
  setBookingList: Dispatch<SetStateAction<BookingListType[]>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  refetch: boolean;
  setRefetch: Dispatch<SetStateAction<boolean>>;
  editDetails: BookingFormType | null;
  setEditDetails: Dispatch<SetStateAction<BookingFormType | null>>;
  bookingSearchParam: BookingParamsType;
  setBookingSearchParams: Dispatch<SetStateAction<BookingParamsType>>;
  setCopyId: Dispatch<
    SetStateAction<{ departmentId: number | null; doctorId: number | null }>
  >;
  copyId: { departmentId: number | null; doctorId: number | null };
};

export type SlotType = { timeSlotNo?: number; times?: string } | undefined;
export type VisitTypesType = {
  id?: number | undefined;
  visitType?: string | number | undefined;
};

export type BookingFormType = {
  id: string | null;
  patientId: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  appointmentBookingDate?: string;
  gender: string;
  dob: string | null;
  age: string;
  ageYears?: number;
  ageMonths?: number;
  ageDays?: number;
  status?: number;
  tokenNo?: number | null;
  mobileNumber: string;
  email: string;
  countryCodeId?: number | null;
  countryCode: CountryType | {
    country_id?: number;
    id?: number | string;
    countryCode: string;
    country_code?: string;
  };
  department?: {
    departmentId: number;
    departmentName: string;
  } | null;
  doctor?: {
    doctorId: number;
    doctorName: string;
    color: string;
  } | null;
  timeSlot?: SlotType | null;
  visitType?: VisitTypesType | null;
  notes?: string;
  startDate?: string | null;
  endDate?: string | null;
  start?: string | null;
  end?: string | null;
  isCompleted: 0 | 1 | 2;
  temporaryBooking?: number;
  reason?: string;
  bookingConfirmed?: number;
  isRegistered?: number;
  roomNo: number | { id: string | number, roomNo: string | number } | string | null;
  journeyStatus?: string;
  isArrived?: number;
  inRoom?: number;
  isEncountered?: number;
  isBilled?: number;
  isCashed?: number;
  noShow?: number;
  bookingCancelled: number;
  isEmrStarted?: number;
  grouping?: string | null;
};

export type SavePayloadType = {
  createdByUserId: number;
  clinicId: number;
  createdDate: string;
  appointmentBookingDate: string;
  temporaryBooking: number
  ip: string;
  id: number | string | null;
  patientId: string | null;
  firstName: string;
  lastName: string;
  middleName: string;
  dob: string | null;
  ageYears: number;
  ageMonths: number;
  ageDays: number;
  gender: string;
  mobile: string;
  email: string;
  departmentId: number;
  doctorId: number;
  appStartTime: string;
  appEndTime: string;
  visitType: number;
  mrn: string;
  status: number;
  tokenNo: number;
  purposeId: number;
  remindSmsDelivry: number;
  notes: string | null;
  timeSlot?: number | null;
  countryId: number | null;
  bookingConfirmed?: number;
  roomNo: number;
  isArrived?: number;
  inRoom?: number;
  isEncountered?: number;
  isCompleted?: number;
  isBilled?: number;
  isCashierDone: number;
  noShow?: number;
  bookingCancelled: number,
  destination: string,
  isEmrStarted?: number;
  encounterId?: number | null;
};

export type SaveParamsType = Omit<
  SavePayloadType,
  "clinicId" | "createdByUserId" | "createdDate" | "ip"
>;

export type CalendarObjectType = {
  id: string;
  title: string;
  color: string;
  start: string;
  end: string;
  allDay: boolean;
  data: BookingFormType;
};

export const initialBooking: PatientBookingContextType = {
  timeSlot: {
    slotType: "dayGridMonth",
    endTime: "76878876",
    timeSlotNo: 0,
    startTime: "",
    times: "",
    visitType: "",
    notes: "",
    email: ""
  },
  setTimeSlot: () => { },
  bookingList: [],
  setBookingList: () => { },
  open: false,
  setOpen: () => { },
  refetch: true,
  setRefetch: () => { },
  editDetails: null,
  setEditDetails: () => { },
  setBookingSearchParams: () => { },
  bookingSearchParam: {
    startTime: "",
    endTime: "",
    department: null,
    doctor: null,
  },
  copyId: { departmentId: null, doctorId: null },
  setCopyId: () => { },
};

export type TimeSlotPayloadType = {
  clinicId: number;
  dateSelected: string;
  dayId: number;
  doctorId: number | null;
};
export type AllTimeSlotPayloadType = {
  clinicId: number;
  dayId: number;
};

export type PatientSearchLisType = {
  MobileNo: string;
  lastName: string;
  gender: string;
  patientId: string;
  isVip: number;
  firstName: string;
  dob: string | null;
  emiratesId: string;
  id: number;
  foDate: string;
  age: string;
  status: number;
};

export type EmergencyContactType = {
  lastName: string;
  contactId: number;
  address2: string;
  address1: string;
  addressType: number;
  mobile: string;
  fullName: string;
  contactTypes: number;
  relationshipId: number;
  contactTypeName: string;
  ecno: number;
  pmasterId: number;
  relationName: string;
  streetAddress: string;
  addressTypeName: string;
};

export type CopayObjectType = {
  deductible: number;
  name: string;
  id: number;
  copay: number;
};

export type InsurancePatientType = {
  insurance: number;
  inPlan_name: string;
  insurancetype_name: string;
  policyNo: string | null;
  insuranceType: number | null;
  cardPhoto2: string;
  cardNo: string;
  cardPhoto: string;
  inGroup_name: string | null;
  createdDate: string;
  policyValidityFrom: string;
  asopNo: string | null;
  id: number;
  insurance_name: string;
  policyValidity: string;
  insuranceGroup: number;
  insurancePlan: number;
};

export type PatientFullDetailsType = {
  image: string | null;
  insurancePatient: InsurancePatientType | null;
  eContactTwo: EmergencyContactType | null;
  companyPatient: string;
  pmaster: {
    lastName: string;
    raceId: number;
    gender: string;
    patientId: string;
    religionId: number;
    languageName: string | null;
    maritalId: number;
    raceName: string | null;
    id: number;
    religionName: string | null;
    gccId: string | null;
    foDate: string;
    patientName: string;
    isEstablished: number;
    maritalName: string;
    languageId: number;
    priority: number;
    grouping: string | null;
    groupingFamily?: string | null;
    isVip: number;
    firstName: string;
    passportNum: string | null;
    dob: string | null;
    middleName: string;
    emiratesId: string;
    globalConsent: number;
    a28_msg_status: number;
    age: string;
    paymentMode: number;
    temporaryBooking: number;
    reason: ""
    bookingConfirmed?: number;
    isRegistered?: number;
  };
  patientDetails: {
    encounterId: number | null;
    doctorId: number | null;
    departmentId: number | null;
    pmasterId?: number | null;
    createdDate?: string | null;
    paymentMode?: number | null;
    purposeId?: number | null;
    purposeName?: string | null;
    referBy?: number | null;
    departmentName?: string | null;
    doctorName?: string | null;
    visitType?: number | null;
  };
  eContactOne: EmergencyContactType | "";
  amaster: {
    clinicCountryName: string;
    countryCodeName?: string | null;
    EmiratesID: string;
    address2: null;
    addressType: number;
    address1: string;
    stateId: number;
    clinicCountryId: number;
    mobileNo: string;
    countryId: number;
    conCode: string;
    stateName: string;
    countryCode: number;
    uploadfile: string;
    patientMasterID: number;
    howKnow: string;
    countryName: string;
    place: string;
    addressMasterID: number;
    email: string;
    photoName: string | null;
  };
  encounter: {
    paymentMode?: number | null;
    encounterId: number | null;
    departmentId: number | null;
    doctorId: number | null;
    pmasterId?: number | null;
    createdDate?: string | null;
    purposeId?: number | null;
    purposeName?: string | null;
    referBy?: number | null;
    departmentName?: string | null;
    doctorName?: string | null;
    visitType?: number | null;
    liscenceNo?: string | null;
    clinicName?: string | null;
    facilityLicenseNo?: number | string | null;
    packageId?: number;
    packageName?: string;
    packageFinalRate?: number | string,
    packageActualRate?: number | string,
    packageCode?: string
    isLabPackage?: number;
    knewAbout?: string;
    referredBy?: string;
    reviewRemarks?: string | null;
    reviewDateTime?: string | null;
  };
  copay: CopayObjectType[];
  postOffice: {
    postofficeId: number;
  };
  temporaryBooking: number;
  reason: string;
  bookingConfirmed: number;
  isRegistered: number
};

export type BillType = {
  doctorName: string;
  date: string;
  billNo: number;
  totalAmount: number;
  balanceAmount: number;
  amountPayed: number;
};

export type CashierType = {
  cashierId: number[];
};

export type BookingSaveResponse = {
  bookingId?: number;
  whatsappMessage?: string;
  [key: string]: string | number | undefined;
};

export const prepareBookingParams = (data: BookingFormType): SaveParamsType => {
  const normalizedDob =
    data.dob && String(data.dob).trim() ? String(data.dob) : null;
  const { fullAge } = normalizedDob
    ? calculateAge(normalizedDob)
    : { fullAge: { years: 0, months: 0, days: 0 } };
  const payload: SaveParamsType = {
    id: parseInt(data.id || "0"),
    firstName: data.firstName,
    middleName: data.middleName,
    lastName: data.lastName,
    gender: data.gender,
    ageDays: fullAge.days,
    temporaryBooking: data.temporaryBooking ? 1 : 0,
    ageMonths: fullAge.months,
    ageYears: fullAge.years,
    appStartTime: toUtcZ(data.startDate) ?? "",
    appEndTime: toUtcZ(data.endDate) ?? "",
    departmentId: data.department?.departmentId as number,
    dob: normalizedDob,
    doctorId: data.doctor?.doctorId as number,
    mobile: data.mobileNumber,
    email: data.email,
    mrn: "",
    notes: data.notes || "",
    patientId: data.patientId || null,
    purposeId: 0,
    remindSmsDelivry: 0,
    status: data.status !== undefined ? data.status : 1,
    tokenNo: 0,
    visitType: data.visitType?.id as number,
    timeSlot: data?.timeSlot?.timeSlotNo,
    countryId: resolveCountryId(data),
    appointmentBookingDate: dayjs.utc(data.startDate).format("DD-MM-YYYY"),
    bookingConfirmed: data.bookingConfirmed !== undefined ? data.bookingConfirmed : 0,
    roomNo: typeof data.roomNo === "object" && data.roomNo !== null ? Number((data.roomNo as any).roomNo) || 0 : Number(data.roomNo) || 0,
    isArrived: data.isArrived !== undefined ? data.isArrived : 0,
    inRoom: data.inRoom !== undefined ? data.inRoom : 0,
    isEncountered: data.isEncountered !== undefined ? data.isEncountered : 0,
    isCompleted: data.isCompleted !== undefined ? data.isCompleted : 0,
    isBilled: data.isBilled !== undefined ? data.isBilled : 0,
    isCashierDone: data.isCashed !== undefined ? data.isCashed : 0,
    noShow: data.noShow !== undefined ? data.noShow : 0,
    isEmrStarted: data.inRoom !== undefined ? data.inRoom : 0,
    bookingCancelled: data.bookingCancelled !== undefined ? data.bookingCancelled : 0,
    destination: data.inRoom == 1 ? "InRoom" : data.isEncountered == 1 ? "Encountered" : data.isBilled == 1 ? "Billed" : data.isCashed == 1 ? "Cashed" : "Booked"
  };
  // console.clear();
  // console.log("data--------->", data);
  // console.log("payload------>", payload);
  return payload;
};
export const prepareConfirmBookingParams = (
  editDetails: BookingFormType,
  selectedStatus?: string,
): SaveParamsType => {

  const statusFlags = {
    bookingConfirmed:
      selectedStatus === "Booking Confirmed" ? 1 : editDetails.bookingConfirmed ?? 0,
    isRegistered:
      selectedStatus === "Registered" ? 1 : editDetails.isRegistered ?? 0,
    isArrived:
      selectedStatus === "Arrived" ? 1 : editDetails.isArrived ?? 0,
    inRoom:
      (selectedStatus === "In Care" || selectedStatus === "In Care") ? 1 : editDetails.inRoom ?? 0,
    isEncountered:
      selectedStatus === "Encountered" ? 1 : editDetails.isEncountered ?? 0,
    isBilled:
      selectedStatus === "Billed" ? 1 : editDetails.isBilled ?? 0,
    isCashed:
      selectedStatus === "Cashed" ? 1 : editDetails.isCashed ?? 0,
    noShow:
      selectedStatus === "No Show" ? 1 : editDetails.noShow ?? 0,
    bookingCancelled:
      selectedStatus === "Booking Cancelled" ? 1 : editDetails.bookingCancelled ?? 0,
  };

  return {
    ...prepareBookingParams({
      ...editDetails,
      ...statusFlags, // ✅ dynamic override
      temporaryBooking: editDetails.patientId ? 0 : 1,
    }),
    id: editDetails.id,
    destination: selectedStatus || "Booked",
  };
};

export type DoctorQueuePatientType = {
  tokenNo: number;
  patientName: string;
  patientId: string;
  status: string;
};

export type DoctorWaitingListType = {
  doctorId: number;
  doctorName: string;
  currentTime: string;
  waitingList: DoctorQueuePatientType[];
  emrStarted: DoctorQueuePatientType[];
  emrSubmitted: DoctorQueuePatientType[];
};

export type UpcomingReviewBookingType = {
  patientDetails: {
    patientMasterId: number;
    encounterId: number;
    patientName: string;
    mrn: string;
    civilId: string;
    mobileNo: string;
  };
  doctorDetails: {
    doctorId: number;
    doctorName: string;
  };
  departmentDetails: {
    departmentId: number;
    departmentName: string;
  };
  reviewRemarks: string;
  reviewDateTime: string;
  reviewStatus: string | null;
};

const splitPatientName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: "", middleName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], middleName: "", lastName: "" };
  }
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: "", lastName: parts[1] };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
};

export const prepareUpcomingReviewBookingParams = ({
  item,
  durationForPatient = 15,
  countryCode,
}: {
  item: UpcomingReviewBookingType;
  durationForPatient?: number;
  countryCode?: BookingFormType["countryCode"];
}): SaveParamsType => {
  const { firstName, middleName, lastName } = splitPatientName(
    item.patientDetails.patientName,
  );
  const slotMinutes = 60 / durationForPatient;
  const startDate = toUtcZ(item.reviewDateTime) ?? item.reviewDateTime;
  const endDate =
    toUtcZ(dayjs.utc(item.reviewDateTime).add(slotMinutes, "minute")) ?? "";

  return {
    ...prepareBookingParams({
      id: null,
      patientId: item.patientDetails.mrn || null,
      firstName,
      middleName,
      lastName,
      gender: "",
      dob: null,
      age: "",
      mobileNumber: item.patientDetails.mobileNo || "",
      email: "",
      countryCode: countryCode || { countryCode: "", id: undefined },
      department: {
        departmentId: item.departmentDetails.departmentId,
        departmentName: item.departmentDetails.departmentName,
      },
      doctor: {
        doctorId: item.doctorDetails.doctorId,
        doctorName: item.doctorDetails.doctorName,
        color: "",
      },
      visitType: { id: 0 },
      notes: item.reviewRemarks || "",
      startDate,
      endDate,
      isCompleted: 0,
      temporaryBooking: 0,
      roomNo: 0,
      bookingCancelled: 0,
      status: 1,
      bookingConfirmed: 1,
    }),
    encounterId: item.patientDetails.encounterId ?? null,
  };
};
